import { environment } from '../../environments/environment';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export class EnvironmentValidator {
  /**
   * Valider la configuration d'environnement
   * @returns Résultat de la validation avec erreurs et avertissements
   */
  static validate(): ValidationResult {
    const result: ValidationResult = {
      isValid: true,
      errors: [],
      warnings: []
    };

    // Vérifier que l'URL de l'API est définie
    if (!environment.apiUrl) {
      result.errors.push('API URL is not defined in environment');
      result.isValid = false;
    } else {
      // Vérifier le format de l'URL de l'API
      try {
        new URL(environment.apiUrl);
      } catch {
        result.errors.push(`Invalid API URL format: ${environment.apiUrl}`);
        result.isValid = false;
      }
    }

    // Vérifier que l'URL du site web est définie
    if (!environment.websiteUrl) {
      result.errors.push('Website URL is not defined in environment');
      result.isValid = false;
    } else {
      // Vérifier le format de l'URL du site web
      try {
        new URL(environment.websiteUrl);
      } catch {
        result.errors.push(`Invalid website URL format: ${environment.websiteUrl}`);
        result.isValid = false;
      }
    }

    // Avertissements pour le développement
    if (!environment.production) {
      if (environment.apiUrl.includes('localhost')) {
        result.warnings.push('Using localhost API URL in development mode');
      }
      
      if (environment.apiUrl.startsWith('http://')) {
        result.warnings.push('API URL uses HTTP instead of HTTPS');
      }
    }

    // Vérifications spécifiques à la production
    if (environment.production) {
      if (environment.apiUrl.includes('dev.') || environment.apiUrl.includes('test.')) {
        result.errors.push('Production environment should not use dev/test URLs');
        result.isValid = false;
      }

      if (environment.apiUrl.startsWith('http://')) {
        result.errors.push('Production environment must use HTTPS');
        result.isValid = false;
      }
    }

    return result;
  }

  /**
   * Afficher les résultats de validation dans la console
   * @param result Résultat de la validation
   */
  static logValidationResults(result: ValidationResult): void {
    if (result.isValid) {
      console.log('✅ Environment configuration is valid');
    } else {
      console.error('❌ Environment configuration has errors:');
      result.errors.forEach(error => console.error(`  - ${error}`));
    }

    if (result.warnings.length > 0) {
      console.warn('⚠️ Environment configuration warnings:');
      result.warnings.forEach(warning => console.warn(`  - ${warning}`));
    }
  }

  /**
   * Valider et afficher les résultats automatiquement
   */
  static validateAndLog(): ValidationResult {
    const result = this.validate();
    this.logValidationResults(result);
    return result;
  }
} 